import { usePrevious } from '@mantine/hooks'
import { debounce } from 'lodash-es'
import { useEffect, useMemo } from 'react'
import { Heading } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'
import CreditMeter from 'src/components/Quota/CreditMeter'
import { UIButton } from 'src/components/ui/Button'
import { UILink } from 'src/components/ui/Link'
import { UIDialog } from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import { GrowthSlot, GrowthSlotName } from 'src/features/Growth/growth-slot'
import { usePaymentMethodAutoRenewable } from 'src/features/Membership/hooks/usePaymentMethodAutoRenewable'
import { useMembershipExpireDialogState } from 'src/features/Membership/utils/expireStateUtils'
import { getSubscriptionState } from 'src/features/Membership/utils/subscriptionStateUtils'
import {
  hasUANewbiePackPurchasableTier,
  isUANewbiePackEntryVisible,
  UANewbiePackEntryPoint,
  useUANewbiePackSnapshot,
} from 'src/features/UAPromotion/useUANewbiePack'
import { useMyInfo } from 'src/services/apollo/auth'
import { useMyMembership } from 'src/services/apollo/membership'
import { useMyQuota } from 'src/services/apollo/quota'
import useDailyClaim, {
  useDailyClaimWithTurnstile,
} from 'src/services/helpers/dailyClaim'
import { useMembershipPlans } from 'src/services/helpers/membership'
import { useMe } from 'src/services/helpers/user'
import { cx } from 'src/utils/cx'
import dayjs from 'src/utils/dayjs'
import { useBreakpoint } from 'src/utils/hooks/useBreakpoint'
import { useCountdownTimer } from 'src/utils/hooks/useCountdownTimer'
import { useLocation } from 'src/utils/router/route'
import { startEventGroup, track } from 'src/utils/tracker'
import { DailyQuestAdUnit } from '../AdUnit/DailyQuestAdUnit'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TrackingProps = Record<string, any>
type QuestDialogProps = {
  onClose?: () => void
  closeAfterClaim?: boolean
  afterClaim?: () => void
  trackingProps?: TrackingProps
}

let opened = false
let openedInSession = false

/**
 * Base method to open the quest dialog
 */
export function openQuestDialog(props?: QuestDialogProps) {
  if (opened) return

  opened = true
  openedInSession = true
  UIDialog.create({
    dismissable: true,
    content: ({ close }) => (
      <DailyQuestsDialogContent
        trackingProps={props?.trackingProps}
        onClose={close}
        onClaimed={() => {
          props?.afterClaim?.()
          if (props?.closeAfterClaim) {
            close()
          }
        }}
      />
    ),
    onClose: () => {
      opened = false
      props?.onClose?.()
    },
  })
}

export const useDailyQuestsDialog = (props?: QuestDialogProps) => {
  const { data: userInfo } = useMyInfo()
  // If user is pending to be deleted, don't show daily quests dialog
  const eligibleToClaim = userInfo && !userInfo.deleteAfter
  const { available } = useDailyClaim()

  const dialogDailyQuests = useMemo(
    () =>
      debounce(() => {
        track('daily_claim_dialog_show')
        openQuestDialog({
          ...props,
          onClose: () => {
            props?.onClose?.()
          },
        })
      }, 200),
    [props],
  )

  useEffect(() => {
    if (!eligibleToClaim || !available) return
    // Skip opening daily quests dialog if onboarding dialog is open
    if (
      document.getElementById('onboarding-dialog-form') ||
      document.getElementById('new-user-dialog')
    )
      return

    if (!openedInSession) {
      dialogDailyQuests()
    }
  }, [available, dialogDailyQuests, eligibleToClaim])
}

function DailyQuestsDialogContent(props: {
  onClose?: () => void
  onClaimed?: () => void
  trackingProps?: TrackingProps
}) {
  const { t } = useTranslation(['user', 'membership'])
  const close = () => props.onClose?.()

  const { data: membership } = useMyMembership()
  const subscriptionState = useMemo(() => {
    return membership?.subscription
      ? getSubscriptionState(membership?.subscription)
      : undefined
  }, [membership])
  const membershipExpireState = useMembershipExpireDialogState(
    {
      nearExpire: [0, 1, 3, 7],
      expired: [0, 1, 3, 7],
    },
    subscriptionState,
  )
  const dayEmphasize = <span className="font-bold" />
  const isAutoRenewable = usePaymentMethodAutoRenewable()

  const { beingMember } = useMe()
  const { data: uaSnapshot } = useUANewbiePackSnapshot()
  const showUAPromoInMembershipSlot = useMemo(() => {
    if (beingMember) return false
    return (
      hasUANewbiePackPurchasableTier(uaSnapshot) &&
      isUANewbiePackEntryVisible(uaSnapshot, UANewbiePackEntryPoint.DailyClaim)
    )
  }, [beingMember, uaSnapshot])

  const { data: quotaData, previousData: previousQuotaData } = useMyQuota()
  const balance = quotaData ?? previousQuotaData
  const { amount } = useDailyClaim()
  const isMsm = useBreakpoint('msm', 'up')

  const MAX_WIDTH = 352 // UIDialog max width - 2 * UIDialog content x padding
  const MIN_WIDTH = 252 // UIDialog min width - 2 * UIDialog content x padding

  const pathname = useLocation().pathname
  const prevPathname = usePrevious(pathname)

  // Close the dialog when route changes
  // TODO: This is a workaround to auto-close the dialog during navigation upon
  //       interaction within the growth slot. Find a better way to handle this.
  if (prevPathname && pathname !== prevPathname) {
    close()
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex justify-between items-start gap-6">
        <Heading className="text-3xl -my-1 font-semibold">
          <Text i18nKey="user:claim-credit-tasks.daily-desc" />
        </Heading>
        <div className="-my-1 flex flex-col items-end">
          <span className="text-xs text-neutral-500">
            <Text i18nKey="artwork:generation.credits-balance" />
          </span>
          <span className="text-purple-600">
            <CreditMeter value={balance} />
          </span>
        </div>
      </header>
      <div
        className="relative self-center"
        style={{
          minWidth: MIN_WIDTH,
          // 100vw - (2 * UIDialog content x padding + 2 * UIDialog modal layer x padding)
          width: isMsm ? 'calc(100vw - 96px)' : 'calc(100vw - 64px)',
          maxWidth: MAX_WIDTH,
        }}
      >
        {match(membershipExpireState)
          .with({ type: 'none' }, () => (
            <DailyQuestAdUnit
              fallback={() => (
                <img
                  src={t('user:quest.claim-dialog.figure.src')}
                  alt=""
                  className="flex-none mx-auto object-scale-down object-center w-full"
                  style={{ width: MAX_WIDTH, maxHeight: MAX_WIDTH }}
                />
              )}
            />
          ))
          .with({ type: 'near-expire' }, () => (
            <img
              src={t('membership:membership.expire-notice.near-expire.image')}
              alt=""
              className="flex-none mx-auto object-scale-down object-center w-full"
              style={{ width: MAX_WIDTH, maxHeight: MAX_WIDTH }}
            />
          ))
          .with({ type: 'expired' }, () => (
            <img
              src={t('membership:membership.expire-notice.expired.image')}
              alt=""
              className="flex-none mx-auto object-scale-down object-center w-full"
              style={{ width: MAX_WIDTH, maxHeight: MAX_WIDTH }}
            />
          ))
          .exhaustive()}
      </div>
      <p
        className="self-center text-base text-center text-balance text-neutral-500"
        // To prevent text from expanding the dialog width
        style={{ maxWidth: MAX_WIDTH }}
      >
        {match(membershipExpireState)
          .with({ type: 'none' }, () => (
            <Text
              i18nKey="user:quest.types.daily_reward.description"
              context={beingMember ? 'member' : undefined}
              values={{ amount: amount ? amount.toLocaleString() : '...' }}
            />
          ))
          .with({ type: 'near-expire' }, s => (
            <Text
              i18nKey={`membership:membership.expire-notice.near-expire.${s.paymentProvider}.content`}
              context={s.days === 0 ? 'today' : undefined}
              values={{ count: s.days }}
              components={{
                DayEmphasize: dayEmphasize,
              }}
            />
          ))
          .with({ type: 'expired' }, s => (
            <Text
              i18nKey={`membership:membership.expire-notice.expired.content`}
              context={s.days === 0 ? 'today' : undefined}
              values={{ count: s.days }}
              components={{
                DayEmphasize: dayEmphasize,
              }}
            />
          ))
          .exhaustive()}
      </p>
      {subscriptionState?.state === 'active' &&
        subscriptionState.current.autoRenewable === 'unspecified' &&
        isAutoRenewable(subscriptionState.current.paymentProvider) && (
          <MembershipButton close={close} variant="renew" />
        )}
      {!beingMember &&
        (showUAPromoInMembershipSlot ? (
          <GrowthSlot name={GrowthSlotName.DailyClaimBottom} />
        ) : (
          <MembershipButton close={close} />
        ))}

      <ClaimButton
        close={close}
        trackingProps={props.trackingProps}
        onClaimed={props.onClaimed}
      />

      {!showUAPromoInMembershipSlot && (
        <GrowthSlot name={GrowthSlotName.DailyClaimBottom} />
      )}
    </div>
  )
}

function ClaimButton(
  props: StyleProps & {
    close: () => void
    onClaimed?: () => void
    trackingProps?: TrackingProps
  },
) {
  const {
    TurnstileWidget,
    amount,
    available,
    claim,
    refreshAt,
    turnstileReady,
    emailVerificationRequiredSince,
  } = useDailyClaimWithTurnstile()
  const isEmailVerificationWarning =
    emailVerificationRequiredSince != null &&
    emailVerificationRequiredSince > Date.now()
  const initialDelta = useMemo(
    () => (refreshAt ? refreshAt - Date.now() : 0),
    [refreshAt],
  )
  const refreshAfter = useCountdownTimer(initialDelta)

  return (
    <>
      <TurnstileWidget />
      <UIButton
        size="large"
        busy="auto"
        className={cx('h-12', props.className)}
        style={props.style}
        // Only disable the button if the daily claim is available and the turnstile is not ready
        disabled={available && !turnstileReady}
        onPress={async () => {
          if (available) {
            track('claim_credit_click', {
              quest: 'daily_reward',
              ...props.trackingProps,
            })
            const success = await claim()
            if (success) {
              props.onClaimed?.()
            }
          } else {
            props.close()
          }
        }}
      >
        {available && isEmailVerificationWarning ? (
          <Text
            i18nKey="user:quest.claim-button.label"
            context="verify-email-warning"
          />
        ) : available ? (
          <Text
            i18nKey="common:app-notification-permission-dialog.benefit1"
            values={{ amount: amount ? amount.toLocaleString() : '...' }}
          />
        ) : (
          <div className="flex flex-col items-center">
            <span className="text-sm">
              <Text i18nKey="action:close" />
            </span>
            <span className="text-xs tabular-nums text-neutral-500">
              <Text
                i18nKey="user:quest.types.daily_reward.next-reward-countdown"
                values={{
                  time: dayjs.duration(refreshAfter).format('HH:mm:ss'),
                }}
              />
            </span>
          </div>
        )}
      </UIButton>
    </>
  )
}

function MembershipButton(
  props: StyleProps & { close: () => void; variant?: 'join' | 'renew' },
) {
  const variant = props.variant ?? 'join'
  const { me } = useMe()
  const plans = useMembershipPlans()
  const promoNumber = useMemo(
    () =>
      plans.length
        ? Math.max(...plans.map(plan => plan.privileges.paidCredit as number))
        : undefined,
    [plans],
  )

  // Renew variant targets the user's credits page where auto-renewal can
  // be managed (via MembershipAutoRenewalDialog), instead of pushing existing
  // members to /membership/plans (a "buy a new plan" flow).
  // Build the path with the actual username (matches MyCredits.tsx /
  // QuotaBalance.tsx / NotificationItem.tsx); fall back to /me/credits which
  // goes through MeRoute -> /@{username}/credits if username is unavailable.
  const renewHref = me?.username ? `/@${me.username}/credits` : '/me/credits'
  const href = variant === 'renew' ? renewHref : '/membership/plans'
  const trackingSource =
    variant === 'renew'
      ? 'daily-claim-dialog-renew'
      : 'daily-claim-dialog-title'

  return (
    <UILink
      href={href}
      variant="filled"
      color="theme"
      size="large"
      className={cx('h-12 text-sm text-center rounded-md', props.className)}
      style={props.style}
      onClick={() => {
        startEventGroup('member_source', trackingSource, {
          persistent: true,
        })
        props.close()
      }}
    >
      <Text
        // For the renew variant, fall back to the join key while the
        // SimpleLocalize entries for `renew-membership-hint[_with-amount]`
        // are being rolled out; once translations land, the renew key wins.
        // i18next picks the first key that resolves; see Text.tsx fallback
        // keys handling.
        i18nKey={
          variant === 'renew'
            ? [
                'user:quest.claim-dialog.renew-membership-hint',
                'user:quest.claim-dialog.join-membership-hint',
              ]
            : 'user:quest.claim-dialog.join-membership-hint'
        }
        context={promoNumber ? 'with-amount' : undefined}
        values={{ amount: promoNumber?.toLocaleString() }}
        components={{
          strong: (
            <strong className="text-yellow-300 [text-shadow:0_0_8px_theme(colors.yellow.300/0.5)]" />
          ),
        }}
      />
    </UILink>
  )
}
